import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {debounce} from 'lodash';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Checkbox, Chip,
    CircularProgress,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    Select,
    TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Link from '@mui/material/Link';
import {blue} from '@mui/material/colors';
import {SuppliersTable} from './SuppliersTable.jsx';
import {OneTimeVendorForm} from './OneTimeVendorForm.jsx';
import {ErrorFlow, Config, BrickContext} from 'olympe';
import {ezprOrange} from '../../../_helpers';
import {OneTimeVendor} from '../../../../models/OneTimeVendor';
import {requestStores} from '../../../ListOfPREditors.brick.jsx';
import {ErrorPopup} from '../ErrorPopup.jsx';
import {getVendorCodes, removeLeadingZeros} from '../../../../utils/helpers';
import {useProperty} from '@olympeio/core';
import {PurchaseRequest} from '../../../../models/PurchaseRequest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {LAMBDA_NAMES} from '../../../../utils/constants';
import ClearIcon from '@mui/icons-material/Clear';
import {PunchoutCatalogTable} from './PunchoutCatalogTable.jsx';

/**
 * @param {DefineSupplierContent.propTypes} props
 * @return {Element}
 */
export const DefineSupplierContent = (props) => {
    const env = Config.getParameter('ENV');
    const [OTVCode, CMVCode, MTVCode] = getVendorCodes(env);

    const getSuppliers = props.context.get(LAMBDA_NAMES.GET_SUPPLIERS);
    const currentTab = useProperty(props.context, 'Current tab', false);
    const useRequestStore = requestStores.get(props.prTag);

    const OTVName = useRequestStore((state) => state.OTVName);
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const OTVAddress = useRequestStore((state) => state.OTVAddress);
    const mainAttachments = useRequestStore((state) => state.mainAttachments);
    const deleteMainAttachments = useRequestStore((state) => state.deleteMainAttachments);
    const EPRId = useRequestStore((state) => state.EPRId);

    const OTVPhoneNumber = useRequestStore((state) => state.OTVPhoneNumber);
    const supplierType = useRequestStore((state) => state.supplierType);
    const plant = useRequestStore((state) => state.defaultPlant);
    const deliveryAddress = useRequestStore((state) => state.defaultDeliveryAddress);
    const materials = useRequestStore((state) => state.materials);
    const firstMaterialCode = {label: materials[0]?.name, value: materials[0]?.name.split(' - ')[0]};
    const firstMaterialL3Cat = materials[0]?.l3Category;

    // Used when using PunchOut catalog, we keep the same info
    const PRInfo = {
        plant: plant,
        deliveryAddress: deliveryAddress,
        l3Category: firstMaterialL3Cat,
        materialCode: firstMaterialCode,
    }

    const [suppliers, setSuppliers] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [showOneTimeVendor, setShowOneTimeVendor] = useState(false);
    const [showCollectiveMedicalVendor, setShowCollectiveMedicalVendor] = useState(false);
    const [showMultiTimeVendor, setShowMultiTimeVendor] = useState(false);
    const [attachments, setAttachments] = useState();
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const [loading, setLoading] = useState(false);
    const [OTVInfo, setOTVInfo] = useState(({...OTVAddress, name: OTVName, phoneNumber: OTVPhoneNumber}));

    const [showPunchoutCatalogs, setShowPunchoutCatalogs] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [searchType, setSearchType] = useState('name1');
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [triggerClearFileName, setTriggerClearFileName] = useState(null);
    const [localizedOnly, setLocalizedOnly] = useState(true);
    const [numberOfResults, setNumberOfResults] = useState(0);
    const [chips, setChips] = useState({purchaseOrg: true});
    const chipsRef = useRef(chips);

    const source = useRequestStore((state) => state.source);
    const [isCMVorOTV, setIsCMVorOTV] = useState(false);
    const [disableSearchBar, setDisableSearchBar] = useState(false);
    const disableAll = currentTab === PurchaseRequest.tab.submitted || props.prLocked;

    // This useEffect enable or disable the save button according to the data
    useEffect(() => {
        if (showOneTimeVendor || showCollectiveMedicalVendor || showMultiTimeVendor || showPunchoutCatalogs) {
            setDisableSearchBar(true);
            const shouldBeDisabled = !OTVInfo.name ||
                !OTVInfo.houseNumber ||
                !OTVInfo.street ||
                !OTVInfo.postalCode ||
                !OTVInfo.city ||
                !OTVInfo.state ||
                !OTVInfo.country ||
                (!OTVInfo.attachmentUploaded && mainAttachments.length === 0);
            setDisableSaveButton(shouldBeDisabled);

            if (props.inline && shouldBeDisabled === false && mainAttachments.every((att) => att.topic)) {
                props.onClose(showOneTimeVendor, searchValue, OTVInfo, showCollectiveMedicalVendor, showMultiTimeVendor, attachments);
            }
        } else {
            setDisableSearchBar(false);
        }
    }, [showOneTimeVendor, OTVInfo, showCollectiveMedicalVendor, showMultiTimeVendor, attachments, showPunchoutCatalogs]);

    // This useEffect display the correct form (OTV or CMV) when the popup is open
    useEffect(() => {
        setShowCollectiveMedicalVendor(supplierType === OneTimeVendor.supplierTypes.cmv);
        setShowOneTimeVendor(supplierType === OneTimeVendor.supplierTypes.otv);
        setShowMultiTimeVendor(supplierType === OneTimeVendor.supplierTypes.mtv);
        // removes the searchBar and localized suppliers
        setIsCMVorOTV(source !== 'popup' && (supplierType === OneTimeVendor.supplierTypes.cmv || supplierType === OneTimeVendor.supplierTypes.otv || supplierType === OneTimeVendor.supplierTypes.mtv));
    }, [supplierType]);

    // This useEffect is used to query the suppliers when the chips change
    useEffect(() => {
        chipsRef.current = chips;
        debouncedQuery(searchValue, searchType, localizedOnly);
    }, [chips]);

    const handleSave = () => {
        if (!mainAttachments.every((att) => att.topic)) {
            setShowErrorPopup(true);
            return;
        }
        props.onClose(showOneTimeVendor, searchValue, OTVInfo, showCollectiveMedicalVendor, showMultiTimeVendor, attachments ?? []);
    };

    /**
     * @param {string} queryValue
     * @param {string} queryType
     * @param {boolean} loc
     */
    const querySuppliers = (queryValue, queryType, loc) => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        // query only if search value is long enough
        if ((queryType === 'land1' && queryValue.length < 2) || (queryType !== 'land1' && queryValue.length < 3)) {
            return;
        }

        // start a query
        setIsPerformingQuery(true);
        setDisableSaveButton(true);
        setLoading(true);

        const [controlFlowIn, searchIn, columnIn, companyCodeIn, purchaseOrgIn] = getSuppliers.getInputs();
        const [Success, Error, resultsOut, countOut] = getSuppliers.getOutputs();
        const getSuppliersRunner = props.context.runner(getSuppliers)
            .set(searchIn, queryValue)
            .set(columnIn, queryType)
            .set(companyCodeIn, loc && queryType !== 'bukrs' ? companyCode.value : null)
            .set(purchaseOrgIn, chipsRef.current.purchaseOrg && queryType !== 'ekorg' ? purchaseOrganization.value : null)
            .trigger(controlFlowIn);

        Promise.any([
            getSuppliersRunner.waitFor(Success),
            getSuppliersRunner.waitFor(Error),
        ]).then((outputFlow) => {
            if (outputFlow instanceof ErrorFlow) {
                setLoading(false);
            } else {
                const receivedSuppliers = JSON.parse(getSuppliersRunner.get(resultsOut));
                setSuppliers(receivedSuppliers);
                setNumberOfResults(getSuppliersRunner.get(countOut));
                setLoading(false);
                setShowOneTimeVendor(false);
                setShowCollectiveMedicalVendor(false);
                setShowMultiTimeVendor(false);
                setShowTable(true);
            }
            setIsPerformingQuery(false);
        })
            .finally(() => getSuppliersRunner.destroy());
    };
    const debouncedQuery = useMemo(() => debounce(querySuppliers, 800), []);

    const handleChangeSearchValue = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        if (value === '') {
            if (props.onClear) {
                props.onClear();
            }
            debouncedQuery('', searchType, localizedOnly);
        } else {
            debouncedQuery(value, searchType, localizedOnly);
        }
    };

    const handleChange = (event) => {
        setSearchType(event.target.value);
        debouncedQuery(searchValue, event.target.value, localizedOnly);
    };

    const handleChangeCheckbox = (event) => {
        const checked = event.target.checked;
        setSearchValue('');
        setDisableSaveButton(!checked);
        setShowTable(false);
        setShowCollectiveMedicalVendor(false);
        setShowMultiTimeVendor(false);
        setShowPunchoutCatalogs(false);
        setShowOneTimeVendor(checked);
        deleteMainAttachments();
        setTriggerClearFileName(false);
        props.onClear && props.onClear();
    };

    const handleChangeCollectiveMedicalCheckbox = (event) => {
        const checked = event.target.checked;
        setSearchValue('');
        setDisableSaveButton(!checked);
        setShowTable(false);
        setShowOneTimeVendor(false);
        setShowPunchoutCatalogs(false);
        setShowCollectiveMedicalVendor(checked);
        setShowMultiTimeVendor(false);
        deleteMainAttachments();
        setTriggerClearFileName(checked);
        props.onClear && props.onClear();
    };

    const handleChangeMultiTimeVendorCheckbox = (e) => {
        const checked = e.target.checked;
        setSearchValue('');
        setDisableSaveButton(!checked);
        setShowTable(false);
        setShowOneTimeVendor(false);
        setShowCollectiveMedicalVendor(false);
        setShowPunchoutCatalogs(false);
        setShowMultiTimeVendor(checked);
        deleteMainAttachments();
        setTriggerClearFileName(false);
        props.onClear && props.onClear();
    };

    const handleChangePunchout = (event) => {
        const checked = event.target.checked;
        setShowOneTimeVendor(false);
        setShowCollectiveMedicalVendor(false);
        setShowMultiTimeVendor(false);
        setShowPunchoutCatalogs(checked);
        setShowTable(false);
        setSearchValue('');
        deleteMainAttachments();
        props.onClear && props.onClear();
    };

    const handleClickOnTableLine = (code, name) => {
        const isOTVCode = removeLeadingZeros(code) === removeLeadingZeros(OTVCode);
        const isCMVCode = removeLeadingZeros(code) === removeLeadingZeros(CMVCode);
        const isMTVCode = removeLeadingZeros(code) === removeLeadingZeros(MTVCode);

        setShowOneTimeVendor(isOTVCode);
        setShowCollectiveMedicalVendor(isCMVCode);
        setShowMultiTimeVendor(isMTVCode);

        if (code && name) {
            const vendorLabel = code + ' - ' + name;
            setSearchValue(vendorLabel);
            setDisableSaveButton(false);
            if (props.inline === true) {
                props.onClose(isOTVCode, vendorLabel, OTVInfo, isCMVCode, isMTVCode, attachments ?? []);
            }
        }
        setShowTable(false);
    };

    const handleChangeLocalizedOnly = (event) => {
        const checked = event.target.checked;
        setLocalizedOnly(checked);

        if (checked) {
            setChips((prevChips) => ({...prevChips, purchaseOrg: true}));
        }
        debouncedQuery(searchValue, searchType, checked);
    };

    const handleAttachmentsChange = (attachments) => {
        if (attachments) {
            setAttachments(attachments);
        } else {
            props.onClear && props.onClear();
        }
    };

    const handleChipToggle = (chipKey) => {
        setChips((prevChips) => {
            const newChips = {...prevChips, [chipKey]: !prevChips[chipKey]};

            if (chipKey === 'purchaseOrg' && !newChips[chipKey] && localizedOnly) {
                setLocalizedOnly(false);
            }
            return newChips;
        });
    };

    return (
        <>
            <div className={'pr-popup-header-orange'}>
                <DialogTitle sx={{color: '#ffffff'}}>Please Define The Supplier</DialogTitle>
                {props.inline !== true &&
                    <div className={'pr-editor-search-supplier-popup-dropdown-and-icon-block'} style={{marginRight: '0.5rem', marginTop: '0.7rem'}}>
                        <IconButton
                            aria-label="close"
                            onClick={props.onCancel}
                            sx={{
                                color: '#ffffff',
                            }}>
                            <CloseIcon
                                sx={{
                                    width: '25px',
                                    height: '25px',
                                }}
                            />
                        </IconButton>
                    </div>
                }
            </div>
            <DialogContent>
                <div className={'pr-editor-search-supplier-popup-textfield-and-select'}>
                    {!isCMVorOTV && (<>
                        <TextField
                            className={'pr-editor-search-supplier-popup-textfield'}
                            placeholder="Search Company"
                            variant="outlined"
                            value={searchValue}
                            onChange={handleChangeSearchValue}
                            InputProps={{
                                endAdornment:
                                    <InputAdornment position={'end'}>
                                        {loading && <CircularProgress/>}
                                        {searchValue && (
                                            <IconButton
                                                onClick={() => {
                                                    setSearchValue('');
                                                    debouncedQuery('', searchType, localizedOnly);
                                                    if (props.onClear) {
                                                        props.onClear();
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <ClearIcon fontSize="medium" />
                                            </IconButton>
                                        )}
                                    </InputAdornment>,
                            }}
                            disabled={disableSearchBar || disableAll}
                            sx={{marginRight: '10px'}} />
                        <FormControl className={'pr-editor-search-supplier-popup-select'}>
                            <Select
                                placeholder={'By ...'}
                                value={searchType}
                                onChange={handleChange}
                                disabled={disableSearchBar || disableAll}
                            >
                                <MenuItem value={'lifnr'}>By Code</MenuItem>
                                <MenuItem value={'name1'}>By Name</MenuItem>
                                <MenuItem value={'ort01'}>By Region</MenuItem>
                                <MenuItem value={'land1'}>By Country</MenuItem>
                                <MenuItem value={'ekorg'}>By Purchase Org</MenuItem>
                                <MenuItem value={'bukrs'}>By Company Code</MenuItem>
                                <MenuItem value={'ktokk'}>By Account Group</MenuItem>
                                <MenuItem value={'stceg'}>By VAT (tax ID)</MenuItem>
                            </Select>
                        </FormControl>
                    </>)}
                    <FormGroup className={'pr-editor-search-supplier-popup-checkbox'}>
                        <FormControlLabel
                            control={<Checkbox checked={showOneTimeVendor} onChange={handleChangeCheckbox}/>}
                            label={'One time vendor'} disabled={isCMVorOTV || disableAll}/>
                        <FormControlLabel control={<Checkbox checked={showCollectiveMedicalVendor}
                            onChange={handleChangeCollectiveMedicalCheckbox}/>}
                        label={'Collective medical vendor'} disabled={isCMVorOTV || disableAll}/>
                        <FormControlLabel control={<Checkbox checked={showMultiTimeVendor}
                            onChange={handleChangeMultiTimeVendorCheckbox}/>}
                        label={'Multi time vendor'} disabled={isCMVorOTV || disableAll}/>
                        <FormControlLabel control={<Checkbox checked={showPunchoutCatalogs}
                            onChange={handleChangePunchout}/>}
                        disabled={disableAll} label={'Punchout catalogs'}/>
                    </FormGroup>
                </div>
                {showTable &&
                    <>
                        <Box
                            sx={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '5px',
                                minHeight: '2rem',
                                marginLeft: '-0.3rem',
                                marginBottom: '0.5rem',
                            }}
                        >
                            <Chip
                                label={`${purchaseOrganization.label}`}
                                icon={<CheckCircleIcon />}
                                onClick={() => handleChipToggle('purchaseOrg')}
                                sx={{
                                    'margin': '4px',
                                    'color': chips.purchaseOrg ? ezprOrange : 'grey',
                                    'borderColor': chips.purchaseOrg ? ezprOrange : 'grey',
                                    'borderWidth': '1px',
                                    'borderStyle': 'solid',
                                    '& .MuiChip-icon': {
                                        color: chips.purchaseOrg ? ezprOrange : 'grey',
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>
                        <SuppliersTable
                            context={props.context}
                            suppliers={suppliers}
                            handleClick={handleClickOnTableLine}
                            companyCode={companyCode}
                            purchaseOrg={purchaseOrganization}
                            localizedOnly={localizedOnly}
                            count={numberOfResults}
                            disableAll={disableAll}
                            DYNInfo={props.DYNInfo}
                            PRInfo={PRInfo}
                            EPRId={EPRId}
                            punchoutCatalog
                            onClosePunchOut={props.onClosePunchOut}
                        />
                    </>
                }
                {(showOneTimeVendor || showCollectiveMedicalVendor || showMultiTimeVendor) && (
                    <OneTimeVendorForm
                        prTag={props.prTag}
                        OTVInfo={OTVInfo}
                        setOTVInfo={setOTVInfo}
                        showCollectiveMedicalVendor={showCollectiveMedicalVendor}
                        context={props.context}
                        triggerClearFileName={triggerClearFileName}
                        disableAll={disableAll}
                        onChange={handleAttachmentsChange}
                    />
                )}
                {showPunchoutCatalogs && (
                    <PunchoutCatalogTable
                        context={props.context}
                        companyCode={companyCode}
                        purchaseOrg={purchaseOrganization}
                        DYNInfo={props.DYNInfo}
                        PRInfo={PRInfo}
                        EPRId={EPRId}
                        onClosePunchOut={props.onClosePunchOut}
                    />
                )}
                {!showOneTimeVendor && !showCollectiveMedicalVendor && !showMultiTimeVendor && !showPunchoutCatalogs && (<FormControlLabel
                    control={<Checkbox checked={localizedOnly} onChange={handleChangeLocalizedOnly}
                        disabled={disableAll}/>} label={'Show localized suppliers'}/>)}
            </DialogContent>
            {props.inline !== true &&
                <DialogActions sx={{
                    justifyContent: 'space-between',
                    width: '100%',
                    height: '6rem',
                }}>
                    <Box sx={{'ml': '1rem'}}>
                        Click <Link
                            href="https://nestle3.mcoutput.com/s2p-approver/10_masterdata/vendors/vlc-intro.htm?tocpath=Master%20Data%7cVendors%7cVendor%20Lifecycle%7c_____0"
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                                'color': blue[500],
                                'textDecorationColor': blue[500],
                                '&:hover': {
                                    color: blue[700],
                                    textDecorationColor: blue[700],
                                },
                            }}>
                        here
                        </Link> for more details on Vendor Life Cycle process
                    </Box>
                    <Button
                        className={'pr-editor-search-supplier-popup-btn' + (disableSaveButton || disableAll ? '-disabled' : '')}
                        onClick={handleSave} disabled={disableSaveButton || disableAll}
                        sx={{marginRight: '3rem'}}>
                        Save
                    </Button>
                </DialogActions>
            }
            <ErrorPopup
                onClose={() => setShowErrorPopup(false)}
                open={showErrorPopup}
                errorMessages={['Please select a topic for each attachment.']}
                title="Please fill in the mandatory information"
            />
        </>
    );
};

DefineSupplierContent.propTypes = {
    prTag: PropTypes.string.isRequired,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    onClose: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onClear: PropTypes.func,
    prLocked: PropTypes.bool,
    inline: PropTypes.bool,
    DYNInfo: PropTypes.object,
    onClosePunchOut: PropTypes.func,
};
